/**
 * Vocabulary shared by API, web app and MCP tools (CLAUDE.md §3).
 *
 * The Prisma enums mirror these lists. Labels are German because they reach
 * the UI and Claude's tool answers unchanged.
 */

export const BRANDS = ['pebro', 'edelband', 'visualaized'] as const
export type BrandId = (typeof BRANDS)[number]

export const BRAND_INFO: Record<BrandId, { name: string; color: string; rootPath: string }> = {
  pebro: { name: 'pebro', color: '#E87100', rootPath: '[01] PEBRO/[00] AI/content-projects' },
  edelband: { name: 'EDELBAND', color: '#28936A', rootPath: '[02] EDELBAND/00_AI/content-projects' },
  visualaized: { name: 'visualaized', color: '#4F2DFF', rootPath: '[03] VISUALAIZED/00_AI/content-projects' },
}

export const CONTENT_TYPES = [
  'newsletter',
  'social_post',
  'reel',
  'story',
  'anzeige',
  'shooting',
  'messe',
  'website',
  'print',
  'pr',
  'sonstiges',
] as const
export type ContentType = (typeof CONTENT_TYPES)[number]

export const CONTENT_TYPE_LABEL: Record<ContentType, string> = {
  newsletter: 'Newsletter',
  social_post: 'Social Post',
  reel: 'Reel',
  story: 'Story',
  anzeige: 'Anzeige',
  shooting: 'Shooting',
  messe: 'Messe',
  website: 'Website',
  print: 'Print',
  pr: 'PR',
  sonstiges: 'Sonstiges',
}

/** In pipeline order; `verworfen` sits outside the flow. */
export const STATUSES = [
  'idee',
  'geplant',
  'in_arbeit',
  'freigabe',
  'eingeplant',
  'veroeffentlicht',
  'verworfen',
] as const
export type Status = (typeof STATUSES)[number]

export const STATUS_LABEL: Record<Status, string> = {
  idee: 'Idee',
  geplant: 'Geplant',
  in_arbeit: 'In Arbeit',
  freigabe: 'Freigabe',
  eingeplant: 'Eingeplant',
  veroeffentlicht: 'Veröffentlicht',
  verworfen: 'Verworfen',
}

export const CHANNELS = [
  'email',
  'instagram',
  'facebook',
  'linkedin',
  'tiktok',
  'youtube',
  'pinterest',
  'google_business',
  'website',
  'print',
  'messe',
] as const
export type Channel = (typeof CHANNELS)[number]

export const CHANNEL_LABEL: Record<Channel, string> = {
  email: 'E-Mail',
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  pinterest: 'Pinterest',
  google_business: 'Google Business',
  website: 'Website',
  print: 'Print',
  messe: 'Messe',
}

/**
 * Labelling and page rules from content-projects/CLAUDE.md, shown in the entry
 * as hints for the day of publishing. They apply only when the post contains
 * AI material, hence the "falls" — the hub does not know that in advance.
 */
const CHANNEL_HINTS: Partial<Record<Channel, string>> = {
  instagram: 'Instagram: KI-Label setzen, falls KI-Material (in der App – das MCP überträgt es nicht)',
  facebook: 'Facebook: KI-Label nach dem Veröffentlichen manuell setzen, falls KI-Material',
  linkedin: 'LinkedIn: auf die Unternehmensseite posten (linkedinPageId)',
  tiktok: 'TikTok: KI-Kennzeichnung setzen, falls KI-Material',
  youtube: 'YouTube: „Synthetische Inhalte“ setzen, falls KI-Material',
}

export function publishingHints(channels: readonly Channel[]): string[] {
  return channels.map((c) => CHANNEL_HINTS[c]).filter((h): h is string => h !== undefined)
}
